import { gql } from "@apollo/client";
import {
  APPLICANT_ACTIVITY_FIELDS,
  APPLICANT_RATE_FIELDS,
  CHALLENGE_RESPONSES_FIELDS,
  MINIMAL_MISSION_ROLE_FIELDS,
  MINIMAL_PROFILE_FIELDS,
} from "graphql/fragments";

const GET_MISSION_ROLE_APPLICANT = gql`
  ${APPLICANT_RATE_FIELDS}
  ${APPLICANT_ACTIVITY_FIELDS}
  ${MINIMAL_PROFILE_FIELDS}
  ${MINIMAL_MISSION_ROLE_FIELDS}
  ${CHALLENGE_RESPONSES_FIELDS}
  query missionRoleApplicant(
    $id: ID!
    $profileChallengesStatus: ChallengeResponseStatus
    $profileChallengesOrderBy: ChallengeResponseOrderBy
    $includeMissionData: Boolean = false
  ) {
    missionRoleApplicant(id: $id) {
      id
      uuid
      state
      ...ApplicantActivityFields
      appliedAt
      noticePeriod
      availablePartTimeDuringNotice
      applicationChecklist
      acceptedAllRequirements
      acceptedRequirements {
        id
        description
      }
      notSelectedReasons {
        id
        name
      }
      highlights {
        id
        name
      }
      negotiatedSellingRate {
        amount
        currency
        frequency
      }
      negotiatedRate {
        amount
        currency
        frequency
      }
      recommendedRate {
        amount
        currency
        frequency
      }
      lockedRecommendedRate {
        amount
        currency
        frequency
      }
      rateScore
      matchScore
      subScores
      clientNotes
      clientNotesJson
      privateNotes
      privateNotesJson
      mission {
        organization {
          applicationsPin
          name
          slug
        }
      }
      challengesCompletedByProfile {
        ...ChallengeResponseFields
      }
      profile {
        ...MinimalProfileFields
        completedInterviewsCount
        id
        slug
        archetype
        memberSince
        email
        allTimeAssignmentsCount
        disciplines {
          id
          name
        }
        skills {
          id
          name
        }
        challengeResponses(status: $profileChallengesStatus, orderBy: $profileChallengesOrderBy) {
          ...ChallengeResponseFields
        }
        status
        partner {
          id
          name
        }
      }
      missionRole {
        ...MinimalMissionRoleFields
        id
        assessmentId
        archetype
        state
        disciplines {
          id
          name
        }
        primarySkills {
          id
          name
        }
        secondarySkills {
          id
          name
        }
        locations {
          id
          name
        }
        requirements {
          id
          description
        }
      }
      scores {
        matchActivityScore
        weightActivity
        matchExperienceScore
        weightExperience
        matchSkillsScore
        weightSkills
        matchTimeZoneScore
        weightTimeZone
        matchScorePrevious
        vectorRank
        vectorScore
      }
      hourlyRateData {
        consideredHourlyRateUsd
        consideredHourlyRateUsdDelta
        applicantHourlyRateUsd
        minimumApplicantHourlyRateUsd
        minimumAssignedHourlyRateUsd
        missionRoleTargetRateUsd
        negotiatedHourlyRateUsd
        profileHourlyRateUsd
        currency
      }
      missionData @include(if: $includeMissionData) {
        aiNoteSummary
      }
      ...ApplicantRateFields
    }
  }
`;

const GET_MISSION_ROLE_APPLICANT_FOR_CLIENT = gql`
  ${MINIMAL_PROFILE_FIELDS}
  query missionRoleApplicant($id: ID!) {
    missionRoleApplicant(id: $id) {
      id
      state
      appliedAt
      noticePeriod
      availablePartTimeDuringNotice
      acceptedAllRequirements
      acceptedRequirements {
        id
        description
      }
      negotiatedSellingRate {
        amount
        currency
        frequency
      }
      clientNotes
      clientNotesJson
      sellingRateRange
      profile {
        id
        ...MinimalProfileFields
      }
      missionRole {
        id
        name
        assessmentId
        archetype
        state
        requirements {
          id
          description
        }
        disciplines {
          id
          name
        }
        primarySkills {
          id
          name
        }
        secondarySkills {
          id
          name
        }
      }
    }
  }
`;

const GET_MISSION_ROLE_APPLICANT_FOR_MEMBER = gql`
  query missionRoleApplicant($id: ID!) {
    missionRoleApplicant(id: $id) {
      id
      state
      appliedAt
      noticePeriod
      availablePartTimeDuringNotice
      acceptedRequirements {
        id
        description
      }
      applicationChecklist
      acceptedAllRequirements
      rate {
        amount
        currency
        frequency
      }
      recommendedRate {
        amount
        currency
        frequency
      }
      profile {
        id
        slug
      }
      challengesForApplicant {
        id
      }
      missionRole {
        slug
        name
        id
        assessmentId
        workArrangement
        archetype
        state
        engagementTime
        applicationSteps {
          name
          required
        }
        interview {
          id
        }
        challenges {
          id
        }
        timezone {
          name
          value
        }
        requirements {
          id
          description
        }
        mission {
          uuid
          name
          companyDescription
          companyDescriptionJson
          isOrganizationConfidential
          organization {
            name
          }
        }
        disciplines {
          id
          name
        }
        primarySkills {
          id
          name
        }
        secondarySkills {
          id
          name
        }
        locations {
          id
          name
        }
      }
    }
  }
`;

const GET_PUBLIC_MISSION_ROLE_ELIGIBILITY = gql`
  query publicMissionRoleEligibilityFetch($missionRoleId: ID!) {
    publicMissionRoleEligibilityFetch(missionRoleId: $missionRoleId) {
      eligible
      message
      redirectTo
      pendingApplication {
        id
        state
        missionRole {
          id
          name
        }
      }
    }
  }
`;

const GET_SHORTLIST_MISSION_ROLE_APPLICANT_ELIGIBILITY = gql`
  query shortlistMissionRoleApplicantEligibility($id: ID!) {
    shortlistMissionRoleApplicantEligibility(id: $id) {
      eligible
      checklist {
        name
        fulfilled
        allowedToSkip
        reason
        metadata
      }
    }
  }
`;

const GET_PROTECTED_MISSION_ROLE_APPLICANT = gql`
  query missionRoleApplicant($id: ID!) {
    missionRoleApplicant(id: $id) {
      id
      uuid
      noticePeriod
      availablePartTimeDuringNotice
      negotiatedSellingRate {
        amount
        currency
        frequency
      }
      profile {
        uuid
        id
      }
      missionRole {
        id
        name
        assessmentId
        mission {
          name
        }
      }
    }
  }
`;

const GET_PUBLIC_MISSION_ROLE_APPLICANT = gql`
  query publicMissionRoleApplicant($id: ID!) {
    publicMissionRoleApplicant(id: $id) {
      uuid
      missionRole {
        name
      }
      profile {
        firstName
      }
    }
  }
`;

export {
  GET_MISSION_ROLE_APPLICANT,
  GET_MISSION_ROLE_APPLICANT_FOR_MEMBER,
  GET_PUBLIC_MISSION_ROLE_ELIGIBILITY,
  GET_MISSION_ROLE_APPLICANT_FOR_CLIENT,
  GET_PROTECTED_MISSION_ROLE_APPLICANT,
  GET_PUBLIC_MISSION_ROLE_APPLICANT,
  GET_SHORTLIST_MISSION_ROLE_APPLICANT_ELIGIBILITY,
};
